// src/pages/league-dashboard/DraftSetupPage.js
// Standalone Draft Setup page — commissioner only
import React, { useState, useEffect } from 'react';
import { Calendar, AlertCircle, Check, Play, Settings, Users, RefreshCw, ArrowUp, ArrowDown, Trophy, LayoutGrid } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import CommissionerDraftSetup from './CommissionerDraftSetup';
import DraftPicksMatrix from './DraftPicksMatrix';
import MinorLeagueDraftOverride from '../../components/commissioner/MinorLeagueDraftOverride';

// Pre-draft (not_started only) order methods — no 'manual' here, that lives in the force-override below.
// Exported per Bible §14 frontend testing convention so unit tests can
// import and assert against the canonical method list without inline
// reimplementation. Matches the batch 2 ORDER_METHODS export pattern.
export const ORDER_METHODS_PRE_DRAFT = [
  { value: 'linear', label: 'Linear (same order every round)' },
  { value: 'snake', label: 'Snake (reverse order on even rounds)' },
  { value: 'random', label: 'Random (shuffled)' },
  { value: 'standings', label: 'Standings (worst record picks first)' },
  { value: 'lottery', label: 'Lottery (weighted by standings)' },
  { value: 'manual', label: 'Manual (drag to reorder — use Force Override below)' },
];

const DraftSetupPage = ({ leagueId, league, teams }) => {
  const [activeTab, setActiveTab] = useState('regular-draft');

  // ML Draft lifecycle state (extracted from CommissionerDraftSetup)
  const [mlDraft, setMlDraft] = useState(null);
  const [mlDraftLoading, setMlDraftLoading] = useState(false);
  const [mlDraftSaving, setMlDraftSaving] = useState(false);
  const [mlDraftError, setMlDraftError] = useState('');
  const [mlDraftMessage, setMlDraftMessage] = useState('');
  const [mlOrderMethod, setMlOrderMethod] = useState('linear');

  const currentSeason = league?.current_season;

  // DEBUG: Log league prop structure to find where draft settings live
  console.log('🔴 DRAFT SETUP: league keys:', league ? Object.keys(league) : 'null');
  console.log('🔴 DRAFT SETUP: league.league_settings:', league?.league_settings);
  console.log('🔴 DRAFT SETUP: league.draft_r1_method:', league?.draft_r1_method);
  console.log('🔴 DRAFT SETUP: league.settings:', league?.settings);

  // Regular draft order state — initialize from league_settings if saved
  // Try multiple paths since we don't know where the settings land
  const savedR1 = league?.league_settings?.draft_r1_method || league?.draft_r1_method || league?.settings?.draft_r1_method;
  const savedR2 = league?.league_settings?.draft_r2plus_method || league?.draft_r2plus_method || league?.settings?.draft_r2plus_method;
  const [r1Method, setR1Method] = useState(savedR1 || 'manual');
  const [r2PlusMethod, setR2PlusMethod] = useState(savedR2 || 'snake');
  const [teamOrder, setTeamOrder] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState('');
  const [generateError, setGenerateError] = useState('');

  // Standings override state
  const [standingsOrder, setStandingsOrder] = useState([]);
  const [savingStandings, setSavingStandings] = useState(false);
  const [standingsMsg, setStandingsMsg] = useState('');
  const [standingsError, setStandingsError] = useState('');

  // Draft picks matrix state
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState(null);

  useEffect(() => {
    if (teams && teams.length > 0) {
      loadDraftOrder();
      loadSavedStandings();
    }
  }, [leagueId, teams]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSavedStandings = async () => {
    try {
      const prevSeason = currentSeason ? currentSeason - 1 : null;
      if (!prevSeason || !leagueId) {
        setStandingsOrder([...teams]);
        return;
      }

      const response = await leaguesAPI.getSeasonHistory(leagueId);

      if (response?.history) {
        const prevSeasonData = response.history.find(s => s.season === prevSeason);
        if (prevSeasonData?.final_standings?.length > 0) {
          // Map saved standings back to full team objects, sorted by final_rank
          const teamMap = Object.fromEntries(teams.map(t => [t.team_id, t]));
          const sorted = [...prevSeasonData.final_standings].sort((a, b) => a.final_rank - b.final_rank);
          const ordered = sorted.map(s => teamMap[s.team_id]).filter(Boolean);
          // Append any teams not in saved standings (new teams added after override)
          const inOrder = new Set(ordered.map(t => t.team_id));
          const missing = teams.filter(t => !inOrder.has(t.team_id));

          if (ordered.length > 0) {
            setStandingsOrder([...ordered, ...missing]);
            return;
          }
        }
      }

      // No saved override — fall back to default team order
      setStandingsOrder([...teams]);
    } catch (err) {
      console.error('Error loading saved standings:', err);
      setStandingsOrder([...teams]);
    }
  };

  const loadDraftOrder = async () => {
    try {
      const response = await leaguesAPI.getDraftOrder(leagueId);
      const picks = response?.draft_picks || [];

      // Load saved R1/R2+ method selections from draft metadata
      const metadata = response?.metadata;
      if (metadata?.round1_method) {
        setR1Method(metadata.round1_method);
      }
      if (metadata?.rounds_2plus_method) {
        setR2PlusMethod(metadata.rounds_2plus_method);
      }

      // Use original_owner for ordering (1 pick per slot, not per current owner)
      // This prevents duplicates when picks are traded
      const r1Picks = picks
        .filter(p => p.round === 1 && p.overall_pick_number != null)
        .sort((a, b) => a.pick_in_round - b.pick_in_round);

      if (r1Picks.length > 0 && teams && teams.length > 0) {
        const teamMap = Object.fromEntries(teams.map(t => [t.team_id, t]));
        // Deduplicate by original_owner — each team appears once in the order
        const seen = new Set();
        const ordered = r1Picks
          .map(p => {
            const originalTeamId = p.original_owner?.team_id || p.original_owner_team_id;
            if (!originalTeamId || seen.has(originalTeamId)) return null;
            seen.add(originalTeamId);
            return teamMap[originalTeamId];
          })
          .filter(Boolean);
        const inOrder = new Set(ordered.map(t => t.team_id));
        const missing = teams.filter(t => !inOrder.has(t.team_id));
        setTeamOrder([...ordered, ...missing]);
      } else {
        setTeamOrder([...teams]);
      }
    } catch {
      setTeamOrder([...teams]);
    }
  };

  useEffect(() => {
    if (currentSeason) {
      loadMLDraftState();
    }
  }, [leagueId, currentSeason]);

  const loadMLDraftState = async () => {
    setMlDraftLoading(true);
    setMlDraftError('');
    try {
      const response = await leaguesAPI.getMinorLeagueDraft(leagueId);
      if (response && (response.draft_id || response.draft_status)) {
        setMlDraft(response);
        setMlOrderMethod(response.draft_order_method || 'linear');
      } else {
        setMlDraft(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setMlDraft(null);
      } else {
        setMlDraftError('Failed to load minor league draft');
      }
    } finally {
      setMlDraftLoading(false);
    }
  };

  // Fetch draft picks matrix when Draft Picks tab is active
  const matrixYear = currentSeason;
  const fetchMatrix = async () => {
    if (!leagueId || !matrixYear) return;
    try {
      setMatrixLoading(true);
      setMatrixError(null);
      const response = await leaguesAPI.getDraftPicksMatrix(leagueId, matrixYear);
      if (response.success) {
        setMatrixData(response);
      } else {
        setMatrixError('Failed to load draft picks');
      }
    } catch (err) {
      setMatrixError(err.message || 'Failed to load draft picks');
    } finally {
      setMatrixLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'draft-picks') {
      fetchMatrix();
    }
  }, [activeTab, leagueId, matrixYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleR1MethodChange = async (value) => {
    console.log('🔴 R1 METHOD CHANGE:', value);
    setR1Method(value);
    try {
      const result = await leaguesAPI.updateLeagueSettings(leagueId, { draft_r1_method: value });
      console.log('🔴 R1 SAVE RESULT:', result);
    } catch (err) {
      console.error('🔴 R1 SAVE FAILED:', err);
    }
  };

  const handleR2PlusMethodChange = async (value) => {
    setR2PlusMethod(value);
    try {
      await leaguesAPI.updateLeagueSettings(leagueId, { draft_r2plus_method: value });
    } catch (err) {
      console.error('Failed to save R2+ method:', err);
    }
  };

  const moveTeam = (index, direction) => {
    const newOrder = [...teamOrder];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[index], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[index]];
    setTeamOrder(newOrder);
  };

  const handleGenerateDraftOrder = async () => {
    setGenerating(true);
    setGenerateMsg('');
    setGenerateError('');
    try {
      const manualOrder = r1Method === 'manual' ? teamOrder.map(t => t.team_id) : null;
      const response = await leaguesAPI.generateDraftOrderR1R2(leagueId, r1Method, r2PlusMethod, manualOrder);
      if (response.success) {
        setGenerateMsg(`Draft order generated (R1: ${r1Method}, R2+: ${r2PlusMethod}) — ${response.total_picks} total picks`);
        setTimeout(() => setGenerateMsg(''), 8000);
        // Refresh matrix data so Draft Picks tab shows updated order
        setMatrixData(null);
        fetchMatrix();
      } else {
        setGenerateError(response.message || 'Failed to generate draft order');
      }
    } catch (err) {
      setGenerateError(err.response?.data?.detail || 'Failed to generate draft order');
    } finally {
      setGenerating(false);
    }
  };

  const moveStandingsTeam = (index, direction) => {
    const newOrder = [...standingsOrder];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[index], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[index]];
    setStandingsOrder(newOrder);
  };

  const handleSaveStandings = async () => {
    if (!currentSeason || standingsOrder.length === 0) return;
    setSavingStandings(true);
    setStandingsMsg('');
    setStandingsError('');
    try {
      const previousSeason = currentSeason - 1;
      const standings = standingsOrder.map((team, idx) => ({
        team_id: team.team_id,
        final_rank: idx + 1
      }));
      const response = await leaguesAPI.overrideSeasonStandings(leagueId, previousSeason, standings);
      if (response.success) {
        setStandingsMsg(`${previousSeason} standings saved — ${response.champion} is champion. Lottery and standings draft methods will now use these rankings.`);
        setTimeout(() => setStandingsMsg(''), 10000);
      } else {
        setStandingsError(response.message || 'Failed to save standings');
      }
    } catch (err) {
      setStandingsError(err.response?.data?.detail || 'Failed to save standings');
    } finally {
      setSavingStandings(false);
    }
  };

  const handleInitializeMLDraft = async () => {
    if (!currentSeason) return;
    setMlDraftSaving(true);
    setMlDraftError('');
    try {
      const response = await leaguesAPI.initializeMinorLeagueDraft(leagueId, currentSeason, 2);
      if (response.draft_id) {
        setMlDraftMessage('Minor league draft initialized');
        setTimeout(() => setMlDraftMessage(''), 5000);
        await loadMLDraftState();
      } else {
        setMlDraftError(response.message || 'Failed to initialize draft');
      }
    } catch (err) {
      setMlDraftError(err.response?.data?.detail || 'Failed to initialize draft');
    } finally {
      setMlDraftSaving(false);
    }
  };

  const handleSaveMLDraftOrder = async () => {
    if (!mlDraft) return;
    setMlDraftSaving(true);
    setMlDraftError('');
    try {
      const response = await leaguesAPI.setMinorLeagueDraftOrder(leagueId, mlOrderMethod);
      if (response.success) {
        setMlDraftMessage(`Draft order set to '${mlOrderMethod}'`);
        setTimeout(() => setMlDraftMessage(''), 5000);
        await loadMLDraftState();
      } else {
        setMlDraftError(response.message || 'Failed to set draft order');
      }
    } catch (err) {
      setMlDraftError(err.response?.data?.detail || 'Failed to set draft order');
    } finally {
      setMlDraftSaving(false);
    }
  };

  // Commissioner access check
  if (league?.role !== 'commissioner') {
    return (
      <div className={`${dynastyTheme.components.card.base} p-12`}>
        <div className="text-center space-y-4">
          <AlertCircle className={`w-16 h-16 mx-auto ${dynastyTheme.classes.text.warning}`} />
          <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
            Commissioner Access Only
          </h2>
          <p className={dynastyTheme.classes.text.neutralLight}>
            Only the league commissioner can access draft setup
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — matches ManualOverrides pattern exactly */}
      <div className={dynastyTheme.components.card.highlighted}>
        <div className="py-6 px-6">
          <div className="flex items-center gap-3">
            <Calendar className={`w-8 h-8 ${dynastyTheme.classes.text.primary}`} />
            <h1 className={`text-4xl font-bold ${dynastyTheme.classes.text.white}`}>
              Draft Setup
            </h1>
          </div>
          <p className={`${dynastyTheme.classes.text.neutralLight} mt-2 max-w-3xl`}>
            Configure draft settings, order, and minor league draft for your league.
          </p>
        </div>
      </div>

      {/* Tab Navigation — matches ManualOverrides tab pattern exactly */}
      <div className={dynastyTheme.components.card.base}>
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('regular-draft')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'regular-draft'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Regular Draft
          </button>
          <button
            onClick={() => setActiveTab('ml-draft')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'ml-draft'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Minor League Draft
          </button>
          <button
            onClick={() => setActiveTab('draft-picks')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'draft-picks'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <LayoutGrid className="w-4 h-4 inline mr-2" />
            Draft Picks
          </button>
        </div>
      </div>

      {/* Regular Draft Tab */}
      {activeTab === 'regular-draft' && (
        <div className="space-y-6">
          <CommissionerDraftSetup
            leagueId={leagueId}
            league={league}
            onUpdate={() => {}}
          />

          {/* Previous Season Standings Override */}
          <div className={dynastyTheme.components.card.base}>
            <div className="p-6">
              <h2 className={`font-semibold ${dynastyTheme.classes.text.white} mb-1 flex items-center gap-2`}>
                <Trophy className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                Set Previous Season Standings
              </h2>
              <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
                Set {currentSeason ? currentSeason - 1 : '—'} final standings. These determine draft order for lottery and standings-based methods.
                Rank 1 = champion (best), rank {standingsOrder.length} = last place (picks first).
              </p>

              {standingsMsg && (
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.success}/20 text-green-400 flex items-center gap-2 mb-4 text-sm`}>
                  <Check className="w-4 h-4 flex-shrink-0" />
                  {standingsMsg}
                </div>
              )}
              {standingsError && (
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400 flex items-center gap-2 mb-4 text-sm`}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {standingsError}
                </div>
              )}

              {standingsOrder.length > 0 && (
                <div className="space-y-3">
                  <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg divide-y divide-gray-700`}>
                    {standingsOrder.map((team, idx) => (
                      <div key={team.team_id} className="flex items-center justify-between px-4 py-2">
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-mono w-6 text-right ${idx === 0 ? 'text-yellow-400' : dynastyTheme.classes.text.primary}`}>
                            #{idx + 1}
                          </span>
                          <span className={`${dynastyTheme.classes.text.white} text-sm`}>
                            {team.team_name}
                          </span>
                          {idx === 0 && <span className="text-yellow-400 text-xs">champion</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => moveStandingsTeam(idx, -1)}
                            disabled={idx === 0 || savingStandings}
                            className={`p-1 rounded ${idx === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-700'}`}
                          >
                            <ArrowUp className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
                          </button>
                          <button
                            onClick={() => moveStandingsTeam(idx, 1)}
                            disabled={idx === standingsOrder.length - 1 || savingStandings}
                            className={`p-1 rounded ${idx === standingsOrder.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-700'}`}
                          >
                            <ArrowDown className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={handleSaveStandings}
                    disabled={savingStandings || standingsOrder.length === 0}
                    className={`${dynastyTheme.components.button.primary} px-4 py-2 flex items-center gap-2`}
                  >
                    <Trophy className="w-4 h-4" />
                    {savingStandings ? 'Saving...' : `Save ${currentSeason ? currentSeason - 1 : ''} Standings`}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Draft Order Setup */}
          <div className={dynastyTheme.components.card.base}>
            <div className="p-6">
              <h2 className={`font-semibold ${dynastyTheme.classes.text.white} mb-1 flex items-center gap-2`}>
                <Settings className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                Draft Order Setup
              </h2>
              <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
                Configure Round 1 pick order and how subsequent rounds follow it.
              </p>

              {generateMsg && (
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.success}/20 text-green-400 flex items-center gap-2 mb-4 text-sm`}>
                  <Check className="w-4 h-4 flex-shrink-0" />
                  {generateMsg}
                </div>
              )}
              {generateError && (
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400 flex items-center gap-2 mb-4 text-sm`}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {generateError}
                </div>
              )}

              <div className="space-y-4">
                {/* Round 1 method */}
                <div>
                  <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2 text-sm`}>
                    Round 1 Order Method
                  </label>
                  <select
                    value={r1Method}
                    onChange={e => handleR1MethodChange(e.target.value)}
                    className={dynastyTheme.components.input}
                    disabled={generating}
                  >
                    <option value="manual">Manual (drag to set order below)</option>
                    <option value="lottery">Lottery (weighted by last season standings)</option>
                    <option value="standings">Standings (worst record picks first)</option>
                    <option value="random">Random (shuffled)</option>
                  </select>
                </div>

                {/* Rounds 2+ method */}
                <div>
                  <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2 text-sm`}>
                    Rounds 2+ Order Method
                  </label>
                  <select
                    value={r2PlusMethod}
                    onChange={e => handleR2PlusMethodChange(e.target.value)}
                    className={dynastyTheme.components.input}
                    disabled={generating}
                  >
                    <option value="snake">Snake (reverse on even rounds)</option>
                    <option value="linear">Linear (same order every round)</option>
                  </select>
                </div>

                {/* Team ordering (manual mode only) */}
                {r1Method === 'manual' && teamOrder.length > 0 && (
                  <div>
                    <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2 text-sm`}>
                      Round 1 Pick Order <span className={`font-normal ${dynastyTheme.classes.text.neutralLight}`}>(top picks first)</span>
                    </label>
                    <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg divide-y divide-gray-700`}>
                      {teamOrder.map((team, idx) => (
                        <div key={team.team_id} className="flex items-center justify-between px-4 py-2">
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-mono ${dynastyTheme.classes.text.primary}`}>
                              #{idx + 1}
                            </span>
                            <span className={`${dynastyTheme.classes.text.white} text-sm`}>
                              {team.team_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => moveTeam(idx, -1)}
                              disabled={idx === 0 || generating}
                              className={`p-1 rounded ${idx === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-700'}`}
                            >
                              <ArrowUp className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
                            </button>
                            <button
                              onClick={() => moveTeam(idx, 1)}
                              disabled={idx === teamOrder.length - 1 || generating}
                              className={`p-1 rounded ${idx === teamOrder.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-700'}`}
                            >
                              <ArrowDown className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleGenerateDraftOrder}
                  disabled={generating || (r1Method === 'manual' && teamOrder.length === 0)}
                  className={`${dynastyTheme.components.button.primary} px-4 py-2 flex items-center gap-2`}
                >
                  <Play className="w-4 h-4" />
                  {generating ? 'Generating...' : 'Generate Draft Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Minor League Draft Tab */}
      {activeTab === 'ml-draft' && (
        <div className="space-y-6">
          {/* 2A: ML Draft Lifecycle — initialize + pre-draft order setup */}
          <div className={dynastyTheme.components.card.base}>
            <div className="p-6">
              <h2 className={`font-semibold ${dynastyTheme.classes.text.white} mb-1 flex items-center gap-2`}>
                <Users className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                Minor League Draft Setup
              </h2>
              <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
                {currentSeason} season &bull; 2-round minor league draft
              </p>

              {mlDraftMessage && (
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.success}/20 text-green-400 flex items-center gap-2 mb-4 text-sm`}>
                  <Check className="w-4 h-4 flex-shrink-0" />
                  {mlDraftMessage}
                </div>
              )}
              {mlDraftError && (
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400 flex items-center gap-2 mb-4 text-sm`}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {mlDraftError}
                </div>
              )}

              {mlDraftLoading ? (
                <div className="flex items-center gap-3 py-4">
                  <div className={`w-6 h-6 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`} />
                  <span className={dynastyTheme.classes.text.neutralLight}>Loading minor league draft...</span>
                </div>
              ) : mlDraft === null ? (
                <div className="space-y-4">
                  <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg p-4`}>
                    <p className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
                      No minor league draft exists for the {currentSeason} season.
                    </p>
                  </div>
                  <button
                    onClick={handleInitializeMLDraft}
                    disabled={mlDraftSaving || !currentSeason}
                    className={`${dynastyTheme.components.button.primary} px-4 py-2 flex items-center gap-2`}
                  >
                    <Play className="w-4 h-4" />
                    {mlDraftSaving ? 'Initializing...' : `Initialize ${currentSeason} Minor League Draft`}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Status badge */}
                  <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg p-4 space-y-2 text-sm`}>
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        Status:{' '}
                        <span className={`font-medium ${
                          mlDraft.draft_status === 'completed' ? 'text-green-400' :
                          mlDraft.draft_status === 'in_progress' ? 'text-yellow-400' :
                          dynastyTheme.classes.text.white
                        }`}>
                          {mlDraft.draft_status}
                        </span>
                      </span>
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        Rounds: <span className={dynastyTheme.classes.text.white}>{mlDraft.total_rounds}</span>
                      </span>
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        Teams: <span className={dynastyTheme.classes.text.white}>{mlDraft.draft_order?.length ?? 0}</span>
                      </span>
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        Method: <span className={dynastyTheme.classes.text.white}>{mlDraft.draft_order_method || 'linear'}</span>
                      </span>
                    </div>
                  </div>

                  {/* Pre-draft order method selector (not_started only) */}
                  {mlDraft.draft_status === 'not_started' && (
                    <div className="space-y-3">
                      <div>
                        <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2 text-sm`}>
                          Draft Order Method
                        </label>
                        <select
                          value={mlOrderMethod}
                          onChange={e => setMlOrderMethod(e.target.value)}
                          className={dynastyTheme.components.input}
                          disabled={mlDraftSaving}
                        >
                          {ORDER_METHODS_PRE_DRAFT.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={handleSaveMLDraftOrder}
                        disabled={mlDraftSaving}
                        className={`${dynastyTheme.components.button.primary} px-4 py-2 flex items-center gap-2`}
                      >
                        <Settings className="w-4 h-4" />
                        {mlDraftSaving ? 'Saving...' : 'Set Draft Order'}
                      </button>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={loadMLDraftState}
                      disabled={mlDraftLoading}
                      className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
                    >
                      <RefreshCw className={`w-3 h-3 mr-1 inline ${mlDraftLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 2B: ML Draft Force Override — any draft status, drag-to-reorder, all 6 methods */}
          <MinorLeagueDraftOverride leagueId={leagueId} teams={teams} league={league} />
        </div>
      )}

      {/* Draft Picks Tab */}
      {activeTab === 'draft-picks' && (
        <DraftPicksMatrix
          data={matrixData}
          loading={matrixLoading}
          error={matrixError}
          onRetry={fetchMatrix}
        />
      )}
    </div>
  );
};

export default DraftSetupPage;
